import { getDB, viewOf, type CachedRow, type SyncTable } from './db'
import { syncBus } from '../sync/bus'

/**
 * The device's copy of what the server holds, with unsent local changes laid
 * over the top.
 *
 * `server` and `local` are kept apart on purpose. A round added while offline
 * lives in `local`; when a stale copy of the same order arrives from the
 * server, another till's older version, or an echo of an earlier state, it
 * replaces `server` and the round stays visible. Merging them into one field
 * would let the echo erase work that has not been sent yet.
 */

export async function putServerRow(
  table: SyncTable,
  tenantId: string,
  id: string,
  server: Record<string, unknown>,
  dayKey?: string,
): Promise<void> {
  const db = await getDB()
  const existing = await db.get('rows', [table, id])

  await db.put('rows', {
    table,
    id,
    tenantId,
    server,
    // Local edits survive a server update. They are dropped only when the
    // write that produced them actually lands, see clearLocal.
    local: existing?.local ?? null,
    outboxSeqs: existing?.outboxSeqs ?? [],
    syncError: existing?.syncError,
    dayKey: dayKey ?? existing?.dayKey,
  })
  syncBus.emit('cache-changed', { table })
}

/** Replaces every cached row for a table (and day, where given). */
export async function replaceRows(
  table: SyncTable,
  tenantId: string,
  rows: { id: string; data: Record<string, unknown>; dayKey?: string }[],
  dayKey?: string,
): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('rows', 'readwrite')
  const store = tx.objectStore('rows')

  const incoming = new Set(rows.map((r) => r.id))
  const existing = await store.index('by_table').getAll(table)

  for (const row of existing) {
    if (row.tenantId !== tenantId) continue
    if (dayKey !== undefined && row.dayKey !== dayKey) continue
    // A row the server no longer returns has been deleted or moved out of
    // scope, unless we are still holding writes for it, in which case
    // dropping it would take an unsent sale off the screen.
    if (!incoming.has(row.id) && row.outboxSeqs.length === 0) {
      await store.delete([table, row.id])
    }
  }

  for (const r of rows) {
    const prev = existing.find((e) => e.id === r.id)
    await store.put({
      table,
      id: r.id,
      tenantId,
      server: r.data,
      local: prev?.local ?? null,
      outboxSeqs: prev?.outboxSeqs ?? [],
      syncError: prev?.syncError,
      dayKey: r.dayKey ?? dayKey,
    })
  }

  await tx.done
  syncBus.emit('cache-changed', { table })
}

/** Applies a write locally, before it has reached the server. */
export async function applyLocal(
  table: SyncTable,
  tenantId: string,
  id: string,
  patch: Record<string, unknown>,
  seq: number,
  dayKey?: string,
): Promise<void> {
  const db = await getDB()
  const existing = await db.get('rows', [table, id])

  await db.put('rows', {
    table,
    id,
    tenantId,
    server: existing?.server ?? null,
    local: { ...(existing?.local ?? {}), ...patch },
    outboxSeqs: [...(existing?.outboxSeqs ?? []), seq],
    syncError: existing?.syncError,
    dayKey: dayKey ?? existing?.dayKey ?? (patch.day_key as string | undefined),
  })
  syncBus.emit('cache-changed', { table })
}

/**
 * Brings a row's overlay in line with what is still queued for it.
 *
 * The overlay (`local`) is how this device shows a change before the server
 * has it. It has to go once no queued write for the row remains, otherwise
 * it outlives its purpose and hides every later change made elsewhere. That
 * is what happened: from the Supabase move until 27 Sep 2026 nothing called
 * this, so a till that had opened a table went on showing it open after
 * another device settled it.
 *
 * Seqs are checked against the outbox itself rather than trusted from the
 * row: the queue compacts writes to the same row, and a seq merged away
 * would otherwise pin the overlay for ever.
 *
 * `landed`: the write reached the server. The overlay is folded into
 * `server` before it is dropped, so the screen does not flick back to the
 * pre-write copy while the server's own echo is still on its way. A write
 * that was refused or thrown away is not folded in, the server never took
 * it, and the next pull shows what it did take.
 */
export async function settleRow(
  table: SyncTable,
  id: string,
  landed: boolean,
): Promise<void> {
  const db = await getDB()
  const row = await db.get('rows', [table, id])
  if (!row) return

  const live = await Promise.all(row.outboxSeqs.map((seq) => db.get('outbox', seq)))
  const remaining = row.outboxSeqs.filter((_, i) => live[i] !== undefined)
  if (remaining.length === row.outboxSeqs.length && (remaining.length > 0 || row.local === null)) {
    return
  }

  const done = remaining.length === 0
  await db.put('rows', {
    ...row,
    outboxSeqs: remaining,
    server: done && landed && row.local ? { ...(row.server ?? {}), ...row.local } : row.server,
    local: done ? null : row.local,
  })
  syncBus.emit('cache-changed', { table })
}

/**
 * Clears overlays left behind with nothing queued under them, every device
 * that ran the app before settleRow was wired up has some. Run once at start.
 */
export async function healOrphanedOverlays(): Promise<void> {
  const db = await getDB()
  const rows = await db.getAll('rows')
  for (const row of rows) {
    if (row.local !== null || row.outboxSeqs.length > 0) {
      await settleRow(row.table, row.id, false)
    }
  }
}

/** Marks a row the server refused, so it can be shown in red. */
export async function markRowError(
  table: SyncTable,
  id: string,
  syncError: CachedRow['syncError'],
): Promise<void> {
  const db = await getDB()
  const row = await db.get('rows', [table, id])
  if (!row) return
  await db.put('rows', { ...row, syncError })
  syncBus.emit('cache-changed', { table })
}

/** Everything cached for a table, merged for display. */
export async function readRows(
  table: SyncTable,
  tenantId: string,
  dayKey?: string,
): Promise<Record<string, unknown>[]> {
  const db = await getDB()
  const rows = await db.getAllFromIndex('rows', 'by_table', table)
  return rows
    .filter((r) => r.tenantId === tenantId)
    .filter((r) => dayKey === undefined || r.dayKey === dayKey)
    .map(viewOf)
}

/**
 * Forgets days that are well past.
 *
 * Rows with unsent writes are never dropped, however old, an order that has
 * not reached the server is the one thing that cannot be re-fetched.
 */
export async function pruneOldDays(keep: string[]): Promise<void> {
  const db = await getDB()
  const rows = await db.getAllFromIndex('rows', 'by_table', 'orders')
  const tx = db.transaction('rows', 'readwrite')

  for (const row of rows) {
    if (row.outboxSeqs.length > 0) continue
    if (row.dayKey && !keep.includes(row.dayKey)) {
      await tx.objectStore('rows').delete(['orders', row.id])
    }
  }
  await tx.done
}
